import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice, InvoiceProofStatus } from '@hero/schema';
import { Price } from '../../../../app/Price';
import { PayOnTimeIcon, PayEarlyIcon, ArrowRightIcon, CashIcon } from '@hero/web-icons';
import { TableCell } from '../../../../ui/Table/Table';
import { CellText } from '../../../../app/Table/CellText';
import { PayButton } from '../../../../app/PayButton';

import { RefDisplay } from '../../../../app/Ref';
import { CellAction } from '../../../../app/Table/CellAction';
import { InvoiceStatusBadge } from '../../Status/InvoiceStatusBadge';
import { useInvoiceInfo, useInvoiceDrawerStore } from '@hero/hooks-shared';
import type { InvoiceTableColumn } from '@hero/hooks-shared';
import { useFeature } from '@hero/web-context';
import { FileThumbnail } from '../../../../app/File/shared/FileThumbnail';

/**
 * Proof badge styles — only `pending` and `rejected` render a chip.
 * `approved` is the new default state for carrier uploads and is
 * intentionally chrome-free: the presence of the thumbnail is all
 * the user needs to know "this is on file".
 */
const PROOF_BADGE_STYLES: Record<InvoiceProofStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: '',
  rejected: 'bg-red-100 text-red-700',
};

export interface InvoiceTableCellProps {
  column: InvoiceTableColumn;
  invoice: Invoice;
  displayForUser: 'customer' | 'carrier';
  /** Whether to show days-left / overdue badge on the due column */
  showDaysLeft?: boolean;
  /**
   * Called when the pay action is triggered. On the carrier surface
   * this opens the `PayInvoiceDrawer` (record off-platform payment);
   * on the legacy customer surface it routes to the Hyperpay flow.
   */
  onPayInvoice?: (invoice: Invoice) => void;
  /**
   * @deprecated Per-row "Upload Invoice" button was removed — the
   * proof upload now lives inside `InvoiceDrawer > Summary`. The
   * prop is kept to avoid breaking the manager's existing wiring
   * and may be removed in a future cleanup.
   */
  onUploadProof?: (invoice: Invoice) => void;
  /**
   * Called when the per-row "Finance" chip is clicked (seller-side
   * only). Manager wires it to navigate to the financing apply
   * wizard with the invoice pre-selected via `?invoiceId=`.
   */
  onFinanceInvoice?: (invoice: Invoice) => void;
}

/**
 * InvoiceTableCell — Centralized cell renderer for invoice table columns.
 *
 * Uses CellText for consistent text styling across all tables.
 */
export const InvoiceTableCell: React.FC<InvoiceTableCellProps> = ({
  column,
  invoice,
  displayForUser,
  showDaysLeft = false,
  onPayInvoice,
  onFinanceInvoice,
}) => {
  const { key } = column;
  const openInvoiceDrawer = useInvoiceDrawerStore(s => s.open);
  const { t } = useTranslation(['m-payment', 'm-financing']);
  const subCreateInvoice = useFeature('invoice_module', 'sub_create_invoice');
  const subFinanceInvoice = useFeature('financing_module', 'sub_carrier_apply');
  const {
    canPay,
    isPayer,
    isPaid,
    daysLeft,
    isOverdue,
    isDueToday,
    isPaidOnTime,
    isPaidEarly,
    dueDateDisplay,
    periodFromDisplay,
    periodToDisplay,
    isSameDayInvoice,
    cashbackDaysLeft,
    cashbackPercentage,
    canFinance,
  } = useInvoiceInfo(invoice);

  // Custom render function takes priority
  if (column.render) {
    const content = column.render(invoice, displayForUser);
    if (content === null) return null;
    return <TableCell className={column.className}>{content}</TableCell>;
  }

  const getDaysLeftBadge = (): React.ReactNode => {
    if (!showDaysLeft || daysLeft === null) return undefined;

    // Paid on time (daysToPay === 0)
    if (isPaidOnTime) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">
          <PayOnTimeIcon className="size-3 text-green-600" />
          {t('invoice.due.paid_on_time')}
        </span>
      );
    }

    // Paid early (daysToPay < 0)
    if (isPaidEarly) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">
          <PayEarlyIcon className="size-3 text-green-600" />
          {t('invoice.due.paid_early', { count: Math.abs(daysLeft) })}
        </span>
      );
    }

    // Overdue (unpaid or paid late)
    if (isOverdue) {
      return (
        <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">
          {t('invoice.due.days_late', { count: Math.abs(daysLeft) })}
        </span>
      );
    }

    // Due today
    if (isDueToday) {
      return (
        <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
          {t('invoice.due.due_today')}
        </span>
      );
    }

    // Future — days remaining
    return (
      <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
        {t('invoice.due.days_left', { count: daysLeft })}
      </span>
    );
  };

  const getCellContent = (): React.ReactNode => {
    switch (key) {
      // Ref column — clickable, opens invoice drawer
      case 'ref': {
        const handleRefClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          openInvoiceDrawer(String(invoice._id));
        };
        return (
          <RefDisplay
            refMain={invoice.ref?.main}
            refShort={invoice.ref?.short}
            onClick={handleRefClick}
          />
        );
      }

      // Status column
      case 'status':
        return <InvoiceStatusBadge status={invoice.status} />;

      // Total amount
      case 'total':
        return (
          <CellText
            variant="amount"
            value={<Price amount={invoice.amount.total} currency={invoice.amount.currency} />}
          />
        );

      // Due date — with optional days-left badge
      case 'due':
        return <CellText variant="primary" value={dueDateDisplay} desc={getDaysLeftBadge()} />;

      // Period column — from - to date range, or "Same Day" label
      case 'period':
        return isSameDayInvoice ? (
          <CellText value={periodFromDisplay} desc={t('invoice.fields.same_day')} />
        ) : (
          <CellText value={`${periodFromDisplay} - ${periodToDisplay}`} />
        );

      // Buyer name (legacy: 'payer' column key kept for backward-compat
      // with downstream column configs; resolves to the buyer party).
      case 'payer':
      case 'buyer':
        return <CellText value={invoice.buyer?.name} />;

      // Seller name (legacy: 'receiver' column key kept for backward-compat).
      case 'receiver':
      case 'seller':
        return <CellText value={invoice.seller?.name} />;

      // Remaining amount
      case 'remaining':
        return (
          <CellText
            variant="amount"
            value={<Price amount={invoice.amount.remaining} currency={invoice.amount.currency} />}
          />
        );

      // VAT Invoice column — status + thumbnail (view only), or "Not Ready"
      case 'vat_inv': {
        const proof = invoice.invoiceProof;

        if (proof) {
          const imageSrc = proof.image?.url || proof.image?.upload;
          return (
            <div className="flex items-center gap-2">
              {imageSrc && <FileThumbnail src={imageSrc} size="sm" expandable />}
              {proof.status !== 'approved' && (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    PROOF_BADGE_STYLES[proof.status]
                  }`}>
                  {t(`invoice.proof.status.${proof.status}`)}
                </span>
              )}
            </div>
          );
        }

        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            {t('invoice.proof.not_ready')}
          </span>
        );
      }

      // Actions column — record payment + finance + view icon
      case 'actions': {
        const handleOpenDrawer = () => {
          openInvoiceDrawer(String(invoice._id));
        };

        const isCarrier = displayForUser === 'carrier';

        // Seller-side "Finance now" chip — the per-row twin of the
        // banner mounted in `InvoiceDrawer`. Gated on `canFinance`
        // (which already includes `!invoice.financing` and the
        // status whitelist) so already-financed invoices drop the
        // chip without an extra round-trip. Style mirrors the
        // emerald banner for a single visual language.
        const showFinanceChip =
          isCarrier &&
          canFinance &&
          subFinanceInvoice.isEnabled &&
          !!onFinanceInvoice;

        // "Record payment" — role-neutral on this surface. Either
        // party on the invoice can stamp the AccountTransaction
        // that closes it (see `payInvoiceAPI`); the table already
        // displays the invoice to both roles in their CRM lens.
        const hasRemainingAmount = (invoice.amount?.remaining ?? 0) > 0;
        const showRecordPayment =
          !isPaid && hasRemainingAmount && !!onPayInvoice;

        return (
          <div className="flex items-center justify-end gap-2">
            {/* Customer: Pay Now button (kept for legacy
                customer-dashboard surface that still wires
                a Hyperpay-backed flow). */}
            {!isCarrier && !isPaid && canPay && isPayer && subCreateInvoice.isEnabled && (
              <PayButton
                label={t('m-payment:actions.pay_now')}
                size="sm"
                onClick={() => onPayInvoice?.(invoice)}
                cashbackPercentage={cashbackPercentage}
                cashbackDaysLeft={cashbackDaysLeft}
              />
            )}

            {/* Record payment chip — neutral gray to keep the
                colorful emphasis on the finance chip. */}
            {isCarrier && showRecordPayment && (
              <button
                type="button"
                onClick={() => onPayInvoice?.(invoice)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50">
                <CashIcon className="size-3.5" />
                {t('m-payment:invoice.actions.record_payment', {
                  defaultValue: 'Record payment',
                })}
              </button>
            )}

            {/* Carrier: Finance Now — filled emerald button matching
                the in-drawer `FinanceInvoiceCTA` primary action. */}
            {showFinanceChip && (
              <button
                type="button"
                onClick={() => onFinanceInvoice?.(invoice)}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-2.5 py-1 text-xs font-semibold text-white shadow-md hover:bg-emerald-800">
                {t('m-financing:invoice_row.finance_chip.cta', {
                  defaultValue: 'Finance now',
                })}
                <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
              </button>
            )}

            <CellAction type="view" onClick={handleOpenDrawer} />
          </div>
        );
      }

      default:
        return null;
    }
  };

  const content = getCellContent();
  if (content === null && key !== 'actions') return null;

  // Actions & VAT Invoice columns — stop propagation for interactive cells
  if (key === 'actions' || key === 'vat_inv') {
    return <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>{content}</TableCell>;
  }

  return <TableCell className={column.className}>{content}</TableCell>;
};
